"use client"

/**
 * AlertDialog (docs/THEME.md 2.9): a confirmation that interrupts. Built on the shared Dialog in layout="sections"
 * (no new dependency): role="alertdialog", no X button, an outside press does NOT dismiss it (Escape and Cancel do),
 * first focus lands on Cancel (the first action), Cancel then the confirming action on the right.
 *
 *   <AlertDialog open={open} onOpenChange={setOpen}>
 *     <AlertDialogContent>
 *       <AlertDialogHeader>
 *         <AlertDialogTitle>Remove this participant?</AlertDialogTitle>
 *         <AlertDialogDescription>They leave the call now.</AlertDialogDescription>
 *       </AlertDialogHeader>
 *       <AlertDialogFooter>
 *         <AlertDialogCancel>Cancel</AlertDialogCancel>
 *         <AlertDialogAction tone="danger" onClick={remove}>Remove</AlertDialogAction>
 *       </AlertDialogFooter>
 *     </AlertDialogContent>
 *   </AlertDialog>
 */
import * as React from "react"

import { Button } from "./button"
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  type DialogContentProps,
} from "./dialog"

const AlertDialog = Dialog
const AlertDialogTrigger = DialogTrigger
const AlertDialogHeader = DialogHeader
const AlertDialogBody = DialogBody
const AlertDialogFooter = DialogFooter
const AlertDialogTitle = DialogTitle
const AlertDialogDescription = DialogDescription

type AlertDialogContentProps = Omit<DialogContentProps, "layout" | "hideClose"> & {
  /** A mandatory acknowledgement with no Cancel action can opt into focusing its first action. */
  initialFocus?: "first-action"
}

const AlertDialogContent = React.forwardRef<HTMLDivElement, AlertDialogContentProps>(
  ({ size = "sm", onPointerDownOutside, onOpenAutoFocus, initialFocus, ...props }, ref) => (
    <DialogContent
      ref={ref}
      layout="sections"
      size={size}
      hideClose
      role="alertdialog"
      onOpenAutoFocus={(event) => {
        // Like Radix AlertDialog: focus starts on Cancel, the safe answer.
        onOpenAutoFocus?.(event)
        if (event.defaultPrevented) return
        const cancel = (event.target as HTMLElement | null)?.querySelector<HTMLElement>("[data-alert-cancel]")
        const target = cancel ?? (initialFocus === "first-action"
          ? (event.target as HTMLElement | null)?.querySelector<HTMLElement>("button:not([disabled])")
          : null)
        if (target) {
          event.preventDefault()
          target.focus()
        }
      }}
      onPointerDownOutside={(event) => {
        // An alert is answered, not clicked away. A caller that wants outside-press to cancel handles the event itself.
        if (onPointerDownOutside) onPointerDownOutside(event)
        else event.preventDefault()
      }}
      {...props}
    />
  )
)
AlertDialogContent.displayName = "AlertDialogContent"

type ActionProps = Omit<React.ComponentProps<typeof Button>, "variant"> & {
  /** "danger" for an action that removes or ends something (red), otherwise the primary button. */
  tone?: "default" | "danger"
}

/** The confirming action. It does not close the dialog by itself: the caller closes on success (async-safe). */
const AlertDialogAction = React.forwardRef<HTMLButtonElement, ActionProps>(({ tone = "default", ...props }, ref) => (
  <Button ref={ref} variant={tone === "danger" ? "destructive" : "default"} {...props} />
))
AlertDialogAction.displayName = "AlertDialogAction"

/** Cancel closes the dialog (Radix Close). */
const AlertDialogCancel = React.forwardRef<HTMLButtonElement, Omit<React.ComponentProps<typeof Button>, "variant">>(
  (props, ref) => (
    <DialogClose asChild>
      <Button ref={ref} variant="outline" data-alert-cancel="" {...props} />
    </DialogClose>
  )
)
AlertDialogCancel.displayName = "AlertDialogCancel"

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
